import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, type ArquivoInfo } from "../api";
import ErrorState from "./ErrorState";
import {
  IconSearch,
  IconPlus,
  IconClose,
  IconSparkles,
  IconTrash,
  IconLink,
  IconCheck,
  IconFile,
  IconUpload,
  IconDownload,
  IconCalendar,
} from "../icons";
import { usePodeExcluir } from "../permissoes";
import AgendarEntrevistaModal, { type Agendamento } from "./AgendarEntrevistaModal";
import ConviteEntrevistaModal from "./ConviteEntrevistaModal";

/**
 * TRIAGEM DE CURRÍCULO — a aba do RH.
 *
 * O caminho é: criar a vaga (nome + setor + função) → pedir as PERGUNTAS
 * ESTRATÉGICAS à IA e ajustar o que quiser → copiar o link e mandar no WhatsApp
 * → avaliar quem respondeu.
 *
 * A IA só SUGERE: as perguntas voltam para a tela e só valem depois que o RH
 * salvar. Assim ninguém perde um ajuste feito à mão porque clicou em "gerar" de
 * novo.
 */

interface Pergunta {
  id: string;
  texto: string;
  peso?: number;
}

interface Resposta {
  id: number;
  candidato: string;
  email: string | null;
  telefone: string | null;
  respostas: { id: string; texto: string; resposta: string }[];
  nota: number | null;
  situacao: string;
  observacao: string | null;
  avaliado_por: string | null;
  respondido_em: string;
  /** Currículo e documentos do candidato (enviados por ele ou pelo RH). */
  arquivos?: ArquivoInfo[];
  /** 'Link' = respondeu sozinho; 'RH' = lançado à mão pelo RH. */
  origem?: string;
  criado_por?: string | null;
  /* ENTREVISTA MARCADA (aba Entrevistas). Preenchidos quando a situação vira
     "Entrevista" e o RH escolhe o dia/hora no calendário — ver
     server/triagem.ts → agendarEntrevista. */
  entrevista_id?: number | null;
  /** YYYY-MM-DD (texto, para o fuso não mexer no dia). */
  entrevista_data?: string | null;
  entrevista_horario?: string | null;
  entrevista_etapa?: string | null;
  entrevista_entrevistador?: string | null;
  entrevista_status?: string | null;
}

interface Triagem {
  id: number;
  nome: string;
  setor: string | null;
  cargo: string | null;
  descricao: string | null;
  perguntas: Pergunta[];
  token: string;
  status: string;
  criado_em: string;
  candidatos?: number;
  aguardando?: number;
  aprovados?: number;
  para_entrevista?: number;
  respostas?: Resposta[];
}

// "Desistiu" e "Não compareceu" vêm da aba Entrevistas: quando a entrevista
// termina assim, o desfecho volta para este cartão (ver server/candidato-entrevista.ts).
const SITUACOES = ["Aguardando", "Entrevista", "Aprovado", "Reprovado", "Desistiu", "Não compareceu"];
const PILL: Record<string, string> = {
  Aguardando: "pill-warn",
  Entrevista: "pill-neutral",
  Aprovado: "pill-ok",
  Reprovado: "pill-danger",
  // Cinza, não vermelho: não compareceu/desistiu não é reprovação — a pessoa
  // pode ser chamada de novo numa próxima vaga.
  Desistiu: "pill-neutral",
  "Não compareceu": "pill-neutral",
};

/** O link que vai para o candidato. */
function linkPublico(token: string): string {
  return `${window.location.origin}/triagem/${token}`;
}

function quando(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

/** "2026-08-20" + "14:30" → "20/08/2026 às 14:30" (data já vem como texto). */
function dataHora(data?: string | null, horario?: string | null): string {
  if (!data) return "";
  const [a, m, d] = data.split("-");
  return `${d}/${m}/${a}${horario ? ` às ${horario}` : ""}`;
}

/**
 * Próximo id livre para uma pergunta nova.
 *
 * Era "p" + (tamanho da lista + 1): remover uma pergunta do meio e adicionar
 * outra repetia o id da última, e o link do candidato passava a ligar as duas
 * à MESMA resposta (o que ele digitava numa aparecia na outra). O servidor
 * também garante o id único ao gravar (prepararPerguntas, em server/triagem.ts).
 */
function novoIdPergunta(lista: { id: string }[]): string {
  const usados = new Set(lista.map((p) => p.id));
  for (let n = lista.length + 1; ; n++) {
    if (!usados.has(`p${n}`)) return `p${n}`;
  }
}

function norm(s: unknown): string {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

export default function TriagemView() {
  const podeExcluir = usePodeExcluir(); // só admin apaga registro (regra do Portal)
  const [linhas, setLinhas] = useState<Triagem[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<ApiError | null>(null);
  const [busca, setBusca] = useState("");
  const [nova, setNova] = useState(false);
  const [abrindo, setAbrindo] = useState<number | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      setLinhas(await api.triagens.list());
    } catch (e) {
      setErro(e instanceof ApiError ? e : new ApiError(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const filtradas = useMemo(() => {
    const q = norm(busca);
    if (!q) return linhas;
    return linhas.filter(
      (t) => norm(t.nome).includes(q) || norm(t.setor).includes(q) || norm(t.cargo).includes(q),
    );
  }, [linhas, busca]);

  async function excluir(t: Triagem) {
    if (!confirm(`Excluir a triagem "${t.nome}"?\n\nAs respostas dos candidatos também são apagadas.`)) return;
    try {
      await api.triagens.remove(t.id);
      await carregar();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao excluir.");
    }
  }

  if (erro) return <ErrorState message={erro.message} code={erro.code} onRetry={carregar} />;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar vaga, setor ou função..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          <span className="badge">{filtradas.length} triagem(ns)</span>
          <button className="btn-primary" onClick={() => setNova(true)}>
            <IconPlus size={17} /> Nova triagem
          </button>
        </div>
      </div>

      {aviso && (
        <div className="triagem-aviso">
          {aviso}
          <button className="btn-secondary btn-sm" onClick={() => setAviso(null)}>
            Fechar
          </button>
        </div>
      )}

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando...</div>
        ) : filtradas.length === 0 ? (
          <div className="empty">
            Nenhuma triagem ainda. Clique em <strong>Nova triagem</strong>: informe a vaga e o setor,
            gere as perguntas com a IA e mande o link no WhatsApp do candidato.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Vaga</th>
                <th>Setor</th>
                <th>Função</th>
                <th>Perguntas</th>
                <th>Candidatos</th>
                <th>Status</th>
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map((t) => (
                <tr key={t.id}>
                  <td>
                    <button className="triagem-nome" onClick={() => setAbrindo(t.id)}>
                      {t.nome}
                    </button>
                  </td>
                  <td>{t.setor || "—"}</td>
                  <td>{t.cargo || "—"}</td>
                  <td>{t.perguntas?.length ?? 0}</td>
                  <td>
                    {t.candidatos ?? 0}
                    {(t.aguardando ?? 0) > 0 && (
                      <small className="muted"> · {t.aguardando} a avaliar</small>
                    )}
                  </td>
                  <td>
                    <span className={`pill ${t.status === "Aberta" ? "pill-ok" : "pill-neutral"}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="col-actions">
                    <button className="btn-secondary btn-sm" onClick={() => setAbrindo(t.id)}>
                      Abrir
                    </button>
                    {podeExcluir && (
                      <button
                        className="icon-btn icon-btn-danger"
                        title="Excluir triagem"
                        onClick={() => excluir(t)}
                      >
                        <IconTrash size={17} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {nova && (
        <TriagemModal
          onClose={() => setNova(false)}
          onSaved={async (msg) => {
            setNova(false);
            setAviso(msg);
            await carregar();
          }}
        />
      )}

      {abrindo != null && (
        <TriagemModal
          id={abrindo}
          onClose={() => setAbrindo(null)}
          onSaved={async (msg) => {
            setAviso(msg);
            await carregar();
          }}
        />
      )}
    </div>
  );
}

/* ==================== Modal: criar / editar / avaliar ==================== */

function TriagemModal({
  id,
  onClose,
  onSaved,
}: {
  id?: number;
  onClose: () => void;
  onSaved: (mensagem: string) => Promise<void> | void;
}) {
  const [nome, setNome] = useState("");
  const [setor, setSetor] = useState("");
  const [cargo, setCargo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [perguntas, setPerguntas] = useState<Pergunta[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState("Aberta");
  const [respostas, setRespostas] = useState<Resposta[]>([]);
  const [aba, setAba] = useState<"vaga" | "candidatos">("vaga");
  const [novoCandidato, setNovoCandidato] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.triagens
      .get(id)
      .then((t: Triagem) => {
        setNome(t.nome);
        setSetor(t.setor ?? "");
        setCargo(t.cargo ?? "");
        setDescricao(t.descricao ?? "");
        setPerguntas(t.perguntas ?? []);
        setToken(t.token);
        setStatus(t.status);
        setRespostas(t.respostas ?? []);
      })
      .catch((e) => setErro(e instanceof Error ? e.message : "Erro ao carregar."));
  }, [id]);

  /** Pede as perguntas à IA. Só sugere: nada é gravado antes de salvar. */
  async function gerarComIa() {
    setGerando(true);
    setErro(null);
    try {
      const r = await api.triagens.perguntasIa(id ?? 0, { nome, setor, cargo, descricao });
      const novas: Pergunta[] = r.perguntas ?? [];
      if (novas.length === 0) {
        setErro("A IA não devolveu perguntas. Tente de novo ou escreva as suas.");
        return;
      }
      // Acrescenta sem apagar o que o RH já escreveu. As que já estavam
      // mantêm o id (é por ele que as respostas gravadas se ligam à pergunta);
      // cada nova recebe um id livre.
      setPerguntas((atuais) => {
        const existentes = new Set(atuais.map((p) => norm(p.texto)));
        const acrescentar = novas.filter((p) => !existentes.has(norm(p.texto)));
        const lista = [...atuais];
        for (const nova of acrescentar) lista.push({ ...nova, id: novoIdPergunta(lista) });
        return lista;
      });
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível gerar as perguntas.");
    } finally {
      setGerando(false);
    }
  }

  function alterarPergunta(i: number, texto: string) {
    setPerguntas((atuais) => atuais.map((p, x) => (x === i ? { ...p, texto } : p)));
  }

  async function salvar() {
    setErro(null);
    if (!nome.trim()) {
      setErro("Informe o nome da vaga.");
      return;
    }
    setSalvando(true);
    try {
      const dados = {
        nome: nome.trim(),
        setor: setor.trim(),
        cargo: cargo.trim(),
        descricao: descricao.trim(),
        status,
        perguntas: perguntas.filter((p) => p.texto.trim()),
      };
      if (id) {
        await api.triagens.update(id, dados);
        await onSaved("Triagem atualizada.");
      } else {
        const r = await api.triagens.create(dados);
        setToken(r.token);
        await onSaved("Triagem criada — copie o link e mande no WhatsApp do candidato.");
      }
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  }

  async function copiarLink() {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(linkPublico(token));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Navegador sem permissão de área de transferência: o campo fica à mão
      // para copiar manualmente, então não vale interromper com um alerta.
      setErro("Não consegui copiar sozinho — selecione o link e copie.");
    }
  }

  /** Abre o WhatsApp já com a mensagem pronta. */
  function abrirWhatsApp(telefone?: string | null) {
    if (!token) return;
    const texto =
      `Olá! Somos do RH da SERCAMP.\n\n` +
      `Você está participando do processo para *${nome}*` +
      `${setor ? ` (${setor})` : ""}.\n\n` +
      `Responda as perguntas neste link — leva poucos minutos:\n${linkPublico(token)}`;
    const numero = String(telefone ?? "").replace(/\D/g, "");
    const base = numero
      ? `https://wa.me/${numero.length <= 11 ? `55${numero}` : numero}`
      : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(texto)}`, "_blank", "noopener");
  }

  /**
   * Grava a avaliação do candidato. Com `entrevista` (dia/hora escolhidos no
   * calendário), o servidor também cria/atualiza a entrevista na aba
   * Entrevistas — por isso a triagem é relida: é de lá que vem o que o cartão
   * mostra como já marcado.
   */
  async function avaliar(
    r: Resposta,
    situacao: string,
    nota: string,
    observacao: string,
    entrevista?: Agendamento | null,
  ) {
    try {
      const resp = await api.triagens.avaliar(r.id, {
        situacao,
        nota,
        observacao,
        ...(entrevista ? { entrevista } : {}),
      });
      if (entrevista && id) {
        const t: Triagem = await api.triagens.get(id);
        setRespostas(t.respostas ?? []);
      } else {
        setRespostas((atuais) =>
          atuais.map((x) =>
            x.id === r.id
              ? { ...x, situacao, nota: nota === "" ? null : Number(nota), observacao }
              : x,
          ),
        );
      }
      await onSaved(resp.mensagem ?? "Candidato avaliado.");
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao avaliar.");
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-triagem" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>{id ? nome || "Triagem" : "Nova triagem de currículo"}</h3>
            <p className="modal-sub">
              Crie a vaga, gere as perguntas com a IA e mande o link no WhatsApp do candidato.
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        {id && (
          <div className="triagem-abas">
            <button className={aba === "vaga" ? "ativa" : ""} onClick={() => setAba("vaga")}>
              Vaga e perguntas
            </button>
            <button className={aba === "candidatos" ? "ativa" : ""} onClick={() => setAba("candidatos")}>
              Candidatos ({respostas.length})
            </button>
          </div>
        )}

        <div className="modal-body">
          {aba === "vaga" && (
            <>
              <div className="doc-grid">
                <label className="field field-wide">
                  <span>
                    Nome da vaga <span className="req">*</span>
                  </span>
                  <input
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Ex.: Eletricista — obra Monte Mor"
                  />
                </label>
                <label className="field">
                  <span>Setor</span>
                  <input
                    value={setor}
                    onChange={(e) => setSetor(e.target.value)}
                    placeholder="Ex.: Operacional"
                  />
                </label>
                <label className="field">
                  <span>Função</span>
                  <input
                    value={cargo}
                    onChange={(e) => setCargo(e.target.value)}
                    placeholder="Ex.: Eletricista de manutenção"
                  />
                </label>
                <label className="field field-wide">
                  <span>O que a vaga exige (ajuda a IA a perguntar melhor)</span>
                  <textarea
                    rows={2}
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    placeholder="Ex.: manutenção em subestação até 138 kV, NR-10 e NR-35 em dia, disponibilidade para viagem"
                  />
                </label>
              </div>

              <div className="triagem-perguntas-head">
                <span>
                  Perguntas estratégicas <small className="muted">({perguntas.length})</small>
                </span>
                <div>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => setPerguntas([...perguntas, { id: novoIdPergunta(perguntas), texto: "" }])}
                  >
                    <IconPlus size={15} /> Pergunta
                  </button>
                  <button
                    type="button"
                    className="btn-ia btn-sm"
                    onClick={gerarComIa}
                    disabled={gerando || (!nome.trim() && !cargo.trim())}
                    title={
                      nome.trim() || cargo.trim()
                        ? "Gerar perguntas de acordo com a função"
                        : "Informe a vaga ou a função primeiro"
                    }
                  >
                    <IconSparkles size={15} /> {gerando ? "Gerando…" : "Gerar com IA"}
                  </button>
                </div>
              </div>

              {perguntas.length === 0 ? (
                <p className="muted">
                  Nenhuma pergunta ainda. Use <strong>Gerar com IA</strong> — as perguntas vêm de
                  acordo com a função e você edita o que quiser antes de salvar.
                </p>
              ) : (
                <ol className="triagem-perguntas">
                  {perguntas.map((p, i) => (
                    <li key={p.id}>
                      <textarea
                        rows={2}
                        value={p.texto}
                        onChange={(e) => alterarPergunta(i, e.target.value)}
                        placeholder="Escreva a pergunta"
                      />
                      <button
                        type="button"
                        className="icon-btn icon-btn-danger"
                        title="Remover pergunta"
                        onClick={() => setPerguntas(perguntas.filter((_, x) => x !== i))}
                      >
                        <IconTrash size={16} />
                      </button>
                    </li>
                  ))}
                </ol>
              )}

              {token && (
                <div className="triagem-link">
                  <span>Link do candidato</span>
                  <div className="triagem-link-linha">
                    <input readOnly value={linkPublico(token)} onFocus={(e) => e.target.select()} />
                    <button type="button" className="btn-secondary btn-sm" onClick={copiarLink}>
                      {copiado ? <IconCheck size={15} /> : <IconLink size={15} />}{" "}
                      {copiado ? "Copiado!" : "Copiar"}
                    </button>
                    <button type="button" className="btn-whats btn-sm" onClick={() => abrirWhatsApp()}>
                      Enviar no WhatsApp
                    </button>
                  </div>
                  <small className="muted">
                    O mesmo link serve para todos os candidatos desta vaga. Encerre a triagem para
                    parar de receber respostas.
                  </small>
                </div>
              )}

              {id && (
                <label className="field">
                  <span>Status</span>
                  <select value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="Aberta">Aberta (recebendo candidatos)</option>
                    <option value="Encerrada">Encerrada</option>
                  </select>
                </label>
              )}
            </>
          )}

          {aba === "candidatos" && (
            <>
              <div className="triagem-perguntas-head">
                <span>
                  Candidatos <small className="muted">({respostas.length})</small>
                </span>
                <div>
                  <button
                    type="button"
                    className="btn-primary btn-sm"
                    onClick={() => setNovoCandidato((v) => !v)}
                  >
                    <IconPlus size={15} /> {novoCandidato ? "Cancelar" : "Adicionar candidato"}
                  </button>
                </div>
              </div>

              {/* O currículo nem sempre chega pelo link: o RH lança o candidato
                  aqui e anexa o arquivo no mesmo formulário. */}
              {novoCandidato && id && (
                <NovoCandidatoForm
                  triagemId={id}
                  vagaNome={nome}
                  vagaCargo={cargo}
                  perguntas={perguntas}
                  onCancelar={() => setNovoCandidato(false)}
                  onCriado={async (msg) => {
                    setNovoCandidato(false);
                    const t: Triagem = await api.triagens.get(id);
                    setRespostas(t.respostas ?? []);
                    await onSaved(msg);
                  }}
                />
              )}

              {respostas.length === 0 ? (
                <p className="muted">
                  Ninguém respondeu ainda. Mande o link no WhatsApp — as respostas aparecem aqui. Se
                  o currículo chegou por e-mail, use <strong>Adicionar candidato</strong>.
                </p>
              ) : (
                <div className="triagem-candidatos">
                  {respostas.map((r) => (
                    <CandidatoCard
                      key={r.id}
                      triagemId={id!}
                      vagaNome={nome}
                      vagaCargo={cargo}
                      resposta={r}
                      onAvaliar={avaliar}
                      onWhats={abrirWhatsApp}
                      onRemover={async (resp) => {
                        if (!confirm(`Tirar ${resp.candidato} desta triagem?\n\nO currículo anexado também é apagado.`)) {
                          return;
                        }
                        try {
                          const r = await api.triagens.removerCandidato(id!, resp.id);
                          setRespostas((atuais) => atuais.filter((x) => x.id !== resp.id));
                          await onSaved(r.mensagem ?? "Candidato removido.");
                        } catch (e) {
                          alert(e instanceof Error ? e.message : "Erro ao remover.");
                        }
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {erro && <div className="form-error">{erro}</div>}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={salvando}>
            Fechar
          </button>
          {aba === "vaga" && (
            <button type="button" className="btn-primary" onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando…" : id ? "Salvar alterações" : "Criar e gerar link"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==================== O RH lança um candidato ==================== */

/**
 * CANDIDATO LANÇADO PELO RH — para o currículo que chegou por fora do link
 * (e-mail, WhatsApp, entregue na portaria).
 *
 * O currículo é escolhido aqui e sobe logo depois de gravar o candidato: é o
 * candidato que dá o endereço da pasta. Se o arquivo falhar, o candidato já
 * está criado e o RH anexa pelo cartão dele — nada se perde.
 */
function NovoCandidatoForm({
  triagemId,
  vagaNome,
  vagaCargo,
  perguntas,
  onCancelar,
  onCriado,
}: {
  triagemId: number;
  vagaNome: string;
  vagaCargo: string;
  perguntas: Pergunta[];
  onCancelar: () => void;
  onCriado: (mensagem: string) => Promise<void> | void;
}) {
  const [candidato, setCandidato] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [observacao, setObservacao] = useState("");
  const [situacao, setSituacao] = useState("Aguardando");
  const [respostas, setRespostas] = useState<Record<string, string>>({});
  const [curriculo, setCurriculo] = useState<File | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // O RH pode já lançar o candidato com a entrevista marcada: mesmo popup de
  // calendário do cartão, só que o agendamento viaja junto com o cadastro.
  const [agenda, setAgenda] = useState<Agendamento | null>(null);
  const [agendando, setAgendando] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function salvar() {
    setErro(null);
    if (!candidato.trim()) {
      setErro("Informe o nome do candidato.");
      return;
    }
    if (situacao === "Entrevista" && !agenda) {
      setErro("Escolha o dia e o horário da entrevista.");
      setAgendando(true);
      return;
    }
    setSalvando(true);
    try {
      const r = await api.triagens.criarCandidato(triagemId, {
        candidato: candidato.trim(),
        telefone: telefone.trim(),
        email: email.trim(),
        observacao: observacao.trim(),
        situacao,
        respostas: perguntas.map((p) => ({ id: p.id, resposta: respostas[p.id] ?? "" })),
        ...(situacao === "Entrevista" && agenda ? { entrevista: agenda } : {}),
      });

      let msg = r.mensagem ?? "Candidato adicionado.";
      if (curriculo) {
        try {
          await api.triagens.curriculo.upload(triagemId, r.id, [curriculo]);
          msg = `${candidato.trim()} adicionado com o currículo anexado.`;
        } catch (e) {
          // O candidato já existe: avisa o que falhou sem perder o cadastro.
          msg = `${candidato.trim()} foi adicionado, mas o currículo não subiu (${
            e instanceof Error ? e.message : "erro"
          }). Anexe pelo cartão dele.`;
        }
      }
      await onCriado(msg);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível adicionar o candidato.");
      setSalvando(false);
    }
  }

  return (
    <div className="triagem-novo-candidato">
      <p className="muted">
        Use quando o currículo chegar por e-mail, WhatsApp ou na mão — o candidato entra na vaga do
        mesmo jeito de quem respondeu pelo link, marcado como <strong>lançado pelo RH</strong>.
      </p>

      <div className="doc-grid">
        <label className="field field-wide">
          <span>
            Nome do candidato <span className="req">*</span>
          </span>
          <input
            value={candidato}
            onChange={(e) => setCandidato(e.target.value)}
            placeholder="Nome e sobrenome"
            autoFocus
          />
        </label>
        <label className="field">
          <span>WhatsApp</span>
          <input
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
            placeholder="(19) 90000-0000"
            inputMode="tel"
          />
        </label>
        <label className="field">
          <span>E-mail</span>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="candidato@email.com"
            inputMode="email"
          />
        </label>
        <label className="field">
          <span>Situação</span>
          <select
            value={situacao}
            onChange={(e) => {
              const nova = e.target.value;
              setSituacao(nova);
              if (nova === "Entrevista" && !agenda) setAgendando(true);
            }}
          >
            {SITUACOES.map((s) => (
              <option key={s} value={s}>
                {s === "Entrevista" ? "Entrevista (marca data e vai para a aba Entrevistas)" : s}
              </option>
            ))}
          </select>
          {situacao === "Entrevista" && (
            <button
              type="button"
              className="btn-secondary btn-sm triagem-agenda-botao"
              onClick={() => setAgendando(true)}
            >
              <IconCalendar size={15} />{" "}
              {agenda ? dataHora(agenda.data, agenda.horario) : "Escolher dia e horário"}
            </button>
          )}
        </label>
        <label className="field">
          <span>Currículo</span>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt,.zip"
            onChange={(e) => setCurriculo(e.target.files?.[0] ?? null)}
          />
          {curriculo && (
            <small className="triagem-curriculo-nome">
              {curriculo.name} ({(curriculo.size / 1048576).toFixed(1)} MB)
            </small>
          )}
        </label>
        <label className="field field-wide">
          <span>Observação</span>
          <input
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            placeholder="De onde veio o currículo, indicação, o que já se sabe do candidato"
          />
        </label>
      </div>

      {perguntas.length > 0 && (
        <details className="triagem-novo-respostas">
          <summary>
            Preencher as respostas da triagem ({perguntas.length}) — opcional
          </summary>
          <ol className="triagem-perguntas">
            {perguntas.map((p) => (
              <li key={p.id}>
                <div className="triagem-novo-pergunta">
                  <span className="triagem-pergunta-txt">{p.texto}</span>
                  <textarea
                    rows={2}
                    value={respostas[p.id] ?? ""}
                    onChange={(e) => setRespostas({ ...respostas, [p.id]: e.target.value })}
                    placeholder="O que o candidato respondeu (se já se sabe)"
                  />
                </div>
              </li>
            ))}
          </ol>
        </details>
      )}

      {erro && <div className="form-error">{erro}</div>}

      <div className="triagem-novo-acoes">
        <button type="button" className="btn-secondary btn-sm" onClick={onCancelar} disabled={salvando}>
          Cancelar
        </button>
        <button type="button" className="btn-primary btn-sm" onClick={salvar} disabled={salvando}>
          {salvando ? "Salvando…" : "Adicionar candidato"}
        </button>
      </div>

      {agendando && (
        <AgendarEntrevistaModal
          candidato={candidato.trim() || "Novo candidato"}
          vaga={vagaNome}
          cargo={vagaCargo}
          inicial={agenda}
          onCancelar={() => {
            setAgendando(false);
            if (!agenda) setSituacao("Aguardando");
          }}
          onConfirmar={(a) => {
            setAgenda(a);
            setSituacao("Entrevista");
            setAgendando(false);
            setErro(null);
          }}
        />
      )}
    </div>
  );
}

/* ==================== Currículo do candidato ==================== */

function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * CURRÍCULO E DOCUMENTOS do candidato, dentro do cartão dele.
 *
 * Normalmente o próprio candidato anexa pelo link do WhatsApp; aqui o RH vê o
 * que chegou, abre, apaga — e anexa o currículo que veio por outro caminho
 * (e-mail, entregue na portaria). Fica junto do candidato, não numa pasta solta.
 */
function CurriculoCandidato({
  triagemId,
  respostaId,
  arquivos,
  onMudou,
}: {
  triagemId: number;
  respostaId: number;
  arquivos: ArquivoInfo[];
  onMudou: (lista: ArquivoInfo[]) => void;
}) {
  const podeExcluir = usePodeExcluir(); // só admin apaga arquivo já anexado
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function enviar(files: FileList | null) {
    if (!files || files.length === 0) return;
    setEnviando(true);
    setErro(null);
    try {
      await api.triagens.curriculo.upload(triagemId, respostaId, files);
      onMudou(await api.triagens.curriculo.list(triagemId, respostaId));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível anexar o arquivo.");
    } finally {
      setEnviando(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function remover(nome: string) {
    if (!confirm(`Apagar "${nome}"?`)) return;
    try {
      await api.triagens.curriculo.remove(triagemId, respostaId, nome);
      onMudou(await api.triagens.curriculo.list(triagemId, respostaId));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível apagar.");
    }
  }

  return (
    <div className="triagem-curriculo">
      <div className="triagem-curriculo-topo">
        <span>
          <IconFile size={15} /> Currículo e documentos{" "}
          <small className="muted">({arquivos.length})</small>
        </span>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => fileRef.current?.click()}
          disabled={enviando}
        >
          <IconUpload size={15} /> {enviando ? "Enviando…" : "Anexar currículo"}
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          hidden
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt,.zip"
          onChange={(e) => enviar(e.target.files)}
        />
      </div>

      {arquivos.length === 0 ? (
        <small className="muted">
          Nenhum currículo anexado. O candidato pode anexar pelo próprio link, ou você anexa aqui o
          que chegou por e-mail.
        </small>
      ) : (
        <ul className="triagem-curriculo-lista">
          {arquivos.map((a) => (
            <li key={a.nome}>
              <a
                href={a.url ?? api.triagens.curriculo.url(triagemId, respostaId, a.nome)}
                target="_blank"
                rel="noopener"
                title="Abrir / baixar"
              >
                <IconDownload size={14} /> {a.nome}
              </a>
              <small className="muted">
                {tamanhoLegivel(a.tamanho)} · {new Date(a.enviado_em).toLocaleDateString("pt-BR")}
              </small>
              {podeExcluir && (
                <button
                  type="button"
                  className="icon-btn icon-btn-danger"
                  title="Apagar arquivo"
                  onClick={() => remover(a.nome)}
                >
                  <IconTrash size={15} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {erro && <div className="form-error">{erro}</div>}
    </div>
  );
}

/* ==================== Cartão de um candidato ==================== */

function CandidatoCard({
  triagemId,
  vagaNome,
  vagaCargo,
  resposta,
  onAvaliar,
  onWhats,
  onRemover,
}: {
  triagemId: number;
  vagaNome: string;
  vagaCargo: string;
  resposta: Resposta;
  onAvaliar: (
    r: Resposta,
    situacao: string,
    nota: string,
    observacao: string,
    entrevista?: Agendamento | null,
  ) => Promise<void>;
  onWhats: (telefone?: string | null) => void;
  onRemover: (r: Resposta) => Promise<void>;
}) {
  const podeExcluir = usePodeExcluir(); // tirar o candidato apaga o registro dele
  const [aberto, setAberto] = useState(false);
  const [situacao, setSituacao] = useState(resposta.situacao);
  const [nota, setNota] = useState(resposta.nota == null ? "" : String(resposta.nota));
  const [observacao, setObservacao] = useState(resposta.observacao ?? "");
  const [salvando, setSalvando] = useState(false);
  // Popup do calendário: abre sozinho quando a situação vira "Entrevista" e o
  // candidato ainda não tem dia marcado — é o que leva ele para a outra aba.
  const [agendando, setAgendando] = useState(false);
  // CONVITE DO CANDIDATO. Marcar a entrevista e avisar a pessoa são dois passos
  // do mesmo minuto de trabalho — por isso o convite abre daqui também, e não
  // só da aba Entrevistas: quem acabou de escolher o dia no calendário manda o
  // link sem trocar de tela.
  const [convidando, setConvidando] = useState(false);
  // Currículo: vem junto com a triagem (a pasta é a fonte) e é atualizado aqui
  // sem recarregar tudo quando o RH anexa ou apaga um arquivo.
  const [arquivos, setArquivos] = useState<ArquivoInfo[]>(resposta.arquivos ?? []);

  const marcada = dataHora(resposta.entrevista_data, resposta.entrevista_horario);

  /** Grava a avaliação junto com o dia/hora escolhidos no calendário. */
  async function confirmarAgenda(a: Agendamento) {
    setSalvando(true);
    try {
      await onAvaliar(resposta, "Entrevista", nota, observacao, a);
      setAgendando(false);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <article className="triagem-candidato">
      <header onClick={() => setAberto(!aberto)}>
        <div>
          <strong>{resposta.candidato}</strong>
          <small className="muted">
            {" "}
            · {resposta.telefone || "sem telefone"} · {quando(resposta.respondido_em)}
            {resposta.origem === "RH"
              ? ` · lançado pelo RH${resposta.criado_por ? ` (${resposta.criado_por})` : ""}`
              : " · respondeu pelo link"}
            {arquivos.length > 0 && ` · ${arquivos.length} anexo(s)`}
          </small>
        </div>
        {marcada && (
          <span
            className="pill pill-ok triagem-entrevista-pill"
            title={`Entrevista na aba Entrevistas${
              resposta.entrevista_entrevistador ? ` · ${resposta.entrevista_entrevistador}` : ""
            }`}
          >
            <IconCalendar size={13} /> {marcada}
          </span>
        )}
        {arquivos.length > 0 && (
          <span className="pill pill-neutral triagem-tem-curriculo" title="Currículo anexado">
            <IconFile size={13} /> currículo
          </span>
        )}
        <span className={`pill ${PILL[resposta.situacao] ?? "pill-neutral"}`}>
          {resposta.situacao}
          {resposta.nota != null ? ` · ${resposta.nota}` : ""}
        </span>
      </header>

      {aberto && (
        <div className="triagem-candidato-corpo">
          <ol className="triagem-respostas">
            {resposta.respostas.map((r, i) => (
              <li key={r.id ?? i}>
                <p className="triagem-pergunta-txt">{r.texto}</p>
                <p className="triagem-resposta-txt">{r.resposta?.trim() || "— (não respondeu)"}</p>
              </li>
            ))}
          </ol>

          <CurriculoCandidato
            triagemId={triagemId}
            respostaId={resposta.id}
            arquivos={arquivos}
            onMudou={setArquivos}
          />

          <div className="triagem-avaliar">
            <label className="field">
              <span>Situação</span>
              <select
                value={situacao}
                onChange={(e) => {
                  const nova = e.target.value;
                  setSituacao(nova);
                  // Deu certo na triagem: já pergunta QUANDO é a entrevista. O
                  // candidato só passa para a outra aba com dia e hora.
                  if (nova === "Entrevista") setAgendando(true);
                }}
              >
                {SITUACOES.map((s) => (
                  <option key={s} value={s}>
                    {s === "Entrevista" ? "Entrevista (marca data e vai para a aba Entrevistas)" : s}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Nota (0–10)</span>
              <input
                type="number"
                min="0"
                max="10"
                step="0.5"
                value={nota}
                onChange={(e) => setNota(e.target.value)}
              />
            </label>
            <label className="field field-wide">
              <span>Observação</span>
              <input
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="O que pesou na decisão"
              />
            </label>
            {marcada && (
              <p className="triagem-entrevista-info">
                <IconCalendar size={15} /> Entrevista marcada para <strong>{marcada}</strong>
                {resposta.entrevista_etapa ? ` · etapa ${resposta.entrevista_etapa}` : ""}
                {resposta.entrevista_entrevistador ? ` · com ${resposta.entrevista_entrevistador}` : ""}
                {resposta.entrevista_status ? ` · ${resposta.entrevista_status}` : ""}
                <small className="muted"> — já está na aba Entrevistas.</small>
              </p>
            )}

            <div className="triagem-avaliar-acoes">
              {podeExcluir && (
                <button
                  type="button"
                  className="btn-secondary btn-sm btn-danger-text"
                  onClick={() => onRemover(resposta)}
                  title="Tirar o candidato desta triagem (apaga o currículo anexado)"
                >
                  <IconTrash size={15} /> Remover
                </button>
              )}
              {resposta.telefone && (
                <button type="button" className="btn-whats btn-sm" onClick={() => onWhats(resposta.telefone)}>
                  WhatsApp
                </button>
              )}
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setAgendando(true)}
                title="Escolher o dia e o horário no calendário"
              >
                <IconCalendar size={15} /> {marcada ? "Reagendar" : "Marcar entrevista"}
              </button>
              {/* Só depois que a entrevista existe: o convite é o link DELA. */}
              {resposta.entrevista_id && (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setConvidando(true)}
                  title="Mandar ao candidato o link com dia, horário, endereço e quem procurar na recepção"
                >
                  <IconLink size={15} /> Convite do candidato
                </button>
              )}
              <button
                type="button"
                className="btn-primary btn-sm"
                disabled={salvando}
                onClick={async () => {
                  setSalvando(true);
                  await onAvaliar(resposta, situacao, nota, observacao);
                  setSalvando(false);
                }}
              >
                {salvando ? "Salvando…" : "Salvar avaliação"}
              </button>
            </div>
          </div>
        </div>
      )}

      {agendando && (
        <AgendarEntrevistaModal
          candidato={resposta.candidato}
          vaga={vagaNome}
          cargo={vagaCargo}
          salvando={salvando}
          inicial={
            resposta.entrevista_data
              ? {
                  data: resposta.entrevista_data,
                  horario: resposta.entrevista_horario ?? "",
                  etapa: resposta.entrevista_etapa ?? "RH",
                  entrevistador: resposta.entrevista_entrevistador ?? "",
                }
              : null
          }
          onCancelar={() => {
            setAgendando(false);
            // Desistiu de marcar: a situação volta para a que está gravada,
            // senão o cartão mostraria "Entrevista" sem entrevista nenhuma.
            if (!resposta.entrevista_data) setSituacao(resposta.situacao);
          }}
          onConfirmar={confirmarAgenda}
        />
      )}

      {convidando && resposta.entrevista_id && (
        // O modal só precisa do id — ele busca no servidor o dia, o horário e o
        // local já gravados. Os demais campos vão junto para o cabeçalho não
        // piscar "carregando" com o nome do candidato em branco.
        <ConviteEntrevistaModal
          entrevista={{
            id: resposta.entrevista_id,
            candidato: resposta.candidato,
            cargo: vagaCargo || vagaNome,
            data: resposta.entrevista_data,
            horario: resposta.entrevista_horario,
          }}
          onClose={() => setConvidando(false)}
        />
      )}
    </article>
  );
}
